import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Avatar,
  Link,
  Stack,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

import { MY_PERF_SCROLL_LIST_PB } from "../../Dashboard/layouts";
import GoalsDataDisclaimer from "../GoalsDataDisclaimer";

const ORANGE = "#D96B04";
const ROW_BG = "#F7F9FB";
const INFO_BG = "#E8F3F8";

function initials(name = "") {
  const parts = name.trim().split(/\s+/);
  const a = parts[0]?.[0] ?? "";
  const b = parts[1]?.[0] ?? "";
  return (a + b).toUpperCase();
}

function RepRow({ rep, onExplore }) {
  return (
    <Paper
      elevation={0}
      sx={{
        px: "16px",
        py: "12px",
        borderRadius: "8px",
        backgroundColor: ROW_BG,
        border: `1px solid transparent`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        transition: "border-color 150ms ease, box-shadow 150ms ease",
        "&:hover": {
          borderColor: ORANGE,
          boxShadow: "0 0 0 2px rgba(217,107,4,0.08)",
        },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: "#E56800",
            fontWeight: 700,
            fontSize: 14,
          }}
        >
          {initials(rep.name)}
        </Avatar>

        <Box>
          <Typography
            sx={{
              fontWeight: 700,
              color: "#020202",
              lineHeight: 1.2,
              fontSize: 14,
            }}
          >
            {rep.name}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#838282", mt: 0.3 }}>
            % to Goal: {rep.percent}%
          </Typography>
        </Box>
      </Stack>

      <Link
        component="button"
        underline="none"
        onClick={() => onExplore?.(rep.name)}
        sx={{
          fontSize: 14,
          fontWeight: 600,
          color: ORANGE,
          cursor: "pointer",
          "&:hover": { textDecoration: "underline" },
        }}
      >
        Explore
      </Link>
    </Paper>
  );
}

export default function TerritoryPerformance({
  onTrack = [],
  notOnTrack = [],
  onTrackPercent = 0,
  notOnTrackPercent = 0,
  height = 220,
  onExplore,
  noGoalsDataAvailable = false,
  goalsDataDisclaimerShow = false,
}) {
  const [tab, setTab] = useState(0);

  const reps = useMemo(
    () => (tab === 0 ? onTrack : notOnTrack),
    [tab, onTrack, notOnTrack],
  );

  const infoText =
    tab === 0
      ? `${onTrackPercent}% of sales reps are on track to meet their goal.`
      : `${notOnTrackPercent}% of sales reps are not on track to meet their goal.`;

  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: "10px",
        border: "1px solid #EEF2F6",
        boxShadow: "1px 4px 4px 0px #00000008",
        p: 2.25,
        height: "93%",
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1.25 }}>
        Territory Performance
      </Typography>

      {goalsDataDisclaimerShow && <GoalsDataDisclaimer />}

      {noGoalsDataAvailable ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            px: 2,
            height: "93%",
          }}
        >
          <Typography sx={{ fontSize: 14, color: "#666666", fontWeight: 500 }}>
            Stay tuned - New Goals for current Quarter to be available soon
          </Typography>
        </Box>
      ) : (
        <>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              minHeight: 34,
              "& .MuiTab-root": {
                minHeight: 34,
                textTransform: "none",
                fontWeight: 600,
                color: "#020202",
                px: 0,
                mr: 3,
              },
              "& .Mui-selected": {
                color: ORANGE,
              },
              "& .MuiTab-root.Mui-selected": {
                color: ORANGE,
              },
              "& .MuiTabs-indicator": {
                backgroundColor: ORANGE,
                height: 2,
              },
            }}
          >
            <Tab label="Reps On Track" />
            <Tab label="Reps Not On Track" />
          </Tabs>

          <Box sx={{ height: "1px", bgcolor: "#EEF2F6", my: 1.25 }} />

          <Box
            sx={{
              bgcolor: INFO_BG,
              borderRadius: "8px",
              px: 1.5,
              py: 1.25,
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1.5,
            }}
          >
            <AutoAwesomeIcon sx={{ color: "#396D87" }} />
            <Typography
              sx={{ color: "#396D87", fontWeight: 600, fontSize: 14 }}
            >
              {infoText}
            </Typography>
          </Box>

          <Box
            sx={{
              maxHeight: height,
              minHeight: 200,
              overflowY: "auto",
              pr: 0.5,
              pb: MY_PERF_SCROLL_LIST_PB,
              display: "flex",
              flexDirection: "column",
              gap: 1.25,
            }}
          >
            {reps.length === 0 ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 4,
                }}
              >
                <Typography sx={{ fontSize: 14, color: "#838282" }}>
                  No data found
                </Typography>
              </Box>
            ) : (
              reps.map((rep) => (
                <RepRow key={rep.id} rep={rep} onExplore={onExplore} />
              ))
            )}
          </Box>
        </>
      )}
    </Box>
  );
}
